import React, { useState, useEffect } from 'react'
import { apiGet, apiPost } from '../utils/api'

interface BackupFile {
  name: string
  size: number
  created: string
  modified: string
  type: 'archive' | 'database'
}

interface DashboardStats {
  directories: {
    [key: string]: {
      count: number
      label: string
      icon: string
      color: string
    }
  }
  documents: {
    [key: string]: {
      count: number
      label: string
      icon: string
      color: string
    }
  }
  recentDocuments: {
    [key: string]: any[]
  }
}

interface BackupSchedule {
  enabled: boolean
  time: string // Формат HH:mm
}

const Dashboard: React.FC = () => {
  const [backendStatus, setBackendStatus] = useState<string>('Проверка...')
  const [isLoading, setIsLoading] = useState(true)
  const [isBackingUp, setIsBackingUp] = useState(false)
  const [backupMessage, setBackupMessage] = useState('')
  const [backups, setBackups] = useState<BackupFile[]>([])
  const [isLoadingBackups, setIsLoadingBackups] = useState(true)
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [backupSchedule, setBackupSchedule] = useState<BackupSchedule>({ enabled: false, time: '02:00' })
  const [isSavingSchedule, setIsSavingSchedule] = useState(false)

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch('/api/health')
        if (response.ok) {
          const data = await response.json()
          setBackendStatus(`✅ ${data.message}`)
        } else {
          setBackendStatus('❌ Backend недоступен')
        }
      } catch (error) {
        setBackendStatus('❌ Ошибка подключения к backend')
      } finally {
        setIsLoading(false)
      }
    }

    const loadBackups = async () => {
      try {
        const data = await apiGet<{ backups: BackupFile[] }>('/backup')
        setBackups(data.backups || [])
      } catch (error) {
        console.error('Ошибка загрузки резервных копий:', error)
      } finally {
        setIsLoadingBackups(false)
      }
    }

    const loadDashboardStats = async () => {
      try {
        const data = await apiGet<{ data: DashboardStats }>('/dashboard-stats')
        setDashboardStats(data.data)
      } catch (error) {
        console.error('Ошибка загрузки статистики дашборда:', error)
      } finally {
        setIsLoadingStats(false)
      }
    }

    const loadBackupSchedule = async () => {
      try {
        const data = await apiGet<{ schedule: BackupSchedule }>('/backup/schedule')
        if (data.schedule) {
          setBackupSchedule(data.schedule)
        }
      } catch (error) {
        console.error('Ошибка загрузки расписания резервного копирования:', error)
      }
    }

    checkBackend()
    loadBackups()
    loadDashboardStats()
    loadBackupSchedule()
  }, [])


  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getFileTypeInfo = (type: 'archive' | 'database') => {
    if (type === 'archive') {
      return {
        icon: '📦',
        label: 'Архив',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
      }
    } else {
      return {
        icon: '🗄️',
        label: 'База данных',
        color: 'text-green-600',
        bgColor: 'bg-green-50'
      }
    }
  }

  const handleBackup = async () => {
    setIsBackingUp(true)
    setBackupMessage('')
    
    try {
      const data = await apiPost<{ message: string }>('/backup', {})
      setBackupMessage(`✅ ${data.message}`)
      
      // Перезагружаем список резервных копий
      const backupData = await apiGet<{ backups: BackupFile[] }>('/backup')
      setBackups(backupData.backups || [])
    } catch (error: any) {
      setBackupMessage(`❌ Ошибка: ${error.message || 'Ошибка подключения к серверу'}`)
    } finally {
      setIsBackingUp(false)
      // Очищаем сообщение через 5 секунд
      setTimeout(() => setBackupMessage(''), 5000)
    }
  }

  const handleSaveSchedule = async () => {
    setIsSavingSchedule(true)
    try {
      await apiPost<{ success: boolean; message: string }>('/backup/schedule', backupSchedule)
      setBackupMessage(`✅ Настройки автоматического копирования сохранены`)
      setTimeout(() => setBackupMessage(''), 3000)
    } catch (error: any) {
      setBackupMessage(`❌ Ошибка сохранения настроек: ${error.message || 'Ошибка подключения к серверу'}`)
      setTimeout(() => setBackupMessage(''), 5000)
    } finally {
      setIsSavingSchedule(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Backend Status */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-3 py-3 sm:p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              Backend API
            </h3>
            <p className="text-xs text-gray-600">
              {isLoading ? 'Проверка...' : backendStatus}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Порт: 6001
            </p>
          </div>
        </div>

        {/* Frontend Status */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-3 py-3 sm:p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              Frontend
            </h3>
            <p className="text-xs text-gray-600">
              ✅ React приложение запущено
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Порт: 6002
            </p>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-3 py-3 sm:p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              Система
            </h3>
            <p className="text-xs text-gray-600">
              🚀 Готова к разработке
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Версия: 1.11.2
            </p>
          </div>
        </div>
      </div>

      {/* Справочники и документы */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Справочники */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-3 py-3 sm:p-4">
            <h3 className="text-base font-medium text-gray-900 mb-3">
              📚 Справочники
            </h3>
            {isLoadingStats ? (
              <div className="text-center py-3">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-xs text-gray-500 mt-1">Загрузка...</p>
              </div>
            ) : dashboardStats ? (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(dashboardStats.directories).map(([key, item]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-1.5">
                      <span className="text-sm">{item.icon}</span>
                      <span className="text-xs font-medium text-gray-900">{item.label}</span>
                    </div>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-${item.color}-100 text-${item.color}-800`}>
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-3 text-gray-500">
                <p className="text-xs">Ошибка загрузки данных</p>
              </div>
            )}
          </div>
        </div>

        {/* Документы */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-3 py-3 sm:p-4">
            <h3 className="text-base font-medium text-gray-900 mb-3">
              📄 Документы
            </h3>
            {isLoadingStats ? (
              <div className="text-center py-3">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-xs text-gray-500 mt-1">Загрузка...</p>
              </div>
            ) : dashboardStats ? (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(dashboardStats.documents).map(([key, item]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-1.5">
                      <span className="text-sm">{item.icon}</span>
                      <span className="text-xs font-medium text-gray-900">{item.label}</span>
                    </div>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-${item.color}-100 text-${item.color}-800`}>
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-3 text-gray-500">
                <p className="text-xs">Ошибка загрузки данных</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Быстрые действия
            </h3>
            {/* Настройки автоматического копирования */}
            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={backupSchedule.enabled}
                  onChange={(e) => setBackupSchedule({ ...backupSchedule, enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Автоматическое копирование</span>
              </label>
              {backupSchedule.enabled && (
                <>
                  <input
                    type="time"
                    value={backupSchedule.time}
                    onChange={(e) => setBackupSchedule({ ...backupSchedule, time: e.target.value })}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleSaveSchedule}
                    disabled={isSavingSchedule}
                    className={`px-3 py-1 text-sm rounded ${
                      isSavingSchedule
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    } text-white`}
                  >
                    {isSavingSchedule ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="flex justify-center">
            <button
              onClick={handleBackup}
              disabled={isBackingUp}
              className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg ${
                isBackingUp 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              <span>{isBackingUp ? '⏳' : '💾'}</span>
              <span>{isBackingUp ? 'Создание...' : 'Резервная копия'}</span>
            </button>
          </div>
          {backupSchedule.enabled && (
            <div className="mt-3 text-center">
              <p className="text-xs text-gray-600">
                ⏰ Автоматическое копирование включено на {backupSchedule.time} каждый день
              </p>
            </div>
          )}
          
          {/* Backup Message */}
          {backupMessage && (
            <div className={`mt-4 p-3 rounded-lg ${
              backupMessage.includes('✅') 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {backupMessage}
            </div>
          )}

          {/* Backup Summary */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Последние резервные копии (3 версии)
            </h4>
            {isLoadingBackups ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500 mt-2">Загрузка...</p>
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">Резервные копии не найдены</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {/* Левая колонка - Архивы */}
                <div className="space-y-2">
                  <h5 className="text-xs font-medium text-gray-600 mb-2 text-center">📦 Архивы проекта</h5>
                  {backups
                    .filter(backup => backup.type === 'archive')
                    .slice(0, 3)
                    .map((backup, index) => {
                      const versionNumber = index + 1
                      const typeInfo = getFileTypeInfo(backup.type)
                      
                      return (
                        <div key={backup.name} className={`p-3 ${typeInfo.bgColor} rounded-lg border`}>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-lg">{typeInfo.icon}</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Версия {versionNumber}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              versionNumber === 1 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {versionNumber === 1 ? 'Новая' : `Версия ${versionNumber}`}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate mb-1">
                            {backup.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(backup.created)} • {formatFileSize(backup.size)}
                          </p>
                        </div>
                      )
                    })}
                </div>

                {/* Правая колонка - Базы данных */}
                <div className="space-y-2">
                  <h5 className="text-xs font-medium text-gray-600 mb-2 text-center">🗄️ Базы данных</h5>
                  {backups
                    .filter(backup => backup.type === 'database')
                    .slice(0, 3)
                    .map((backup, index) => {
                      const versionNumber = index + 1
                      const typeInfo = getFileTypeInfo(backup.type)
                      
                      return (
                        <div key={backup.name} className={`p-3 ${typeInfo.bgColor} rounded-lg border`}>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-lg">{typeInfo.icon}</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Версия {versionNumber}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              versionNumber === 1 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {versionNumber === 1 ? 'Новая' : `Версия ${versionNumber}`}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate mb-1">
                            {backup.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(backup.created)} • {formatFileSize(backup.size)}
                          </p>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}
            
            {backups.length > 6 && (
              <p className="text-xs text-gray-500 text-center mt-4">
                И еще {Math.floor((backups.length - 6) / 2)} версий...
              </p>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}

export default Dashboard
